<template>
  <div class="export-form" :class="classModifiers">
    <div class="export-form__fields" :inert="generating">
      <p class="export-form__intro">{{ t("intro") }}</p>

      <sp-alert v-if="showCapWarning" type="warning" :text="capWarningText" icon></sp-alert>
      <sp-alert v-if="requestErrorText" role="alert" type="error" :text="requestErrorText" icon></sp-alert>

      <div class="export-form__consent">
        <sp-checkbox
          v-model="consent"
          :label="t('consent_label')"
          :disabled="generating"
          :data-testid="TestId.directoryPdf.consent"
        ></sp-checkbox>
        <span v-if="fieldErrors.consent" role="alert" class="export-form__error">{{ fieldErrors.consent }}</span>
      </div>
    </div>

    <div v-if="generating" class="export-form__generating" role="status">
      <sp-progress type="circular" size="display" color="primary" :aria-label="t('generating_status')"></sp-progress>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import { t } from "#ce/apps/SpDirectoryPdfExport/i18n";
import { useDirectoryPdfExportStore } from "#ce/apps/SpDirectoryPdfExport/store/export";

const props = defineProps({
  totalCount: {
    type: Number,
    default: 0,
  },
  recordCap: {
    type: Number,
    default: 0,
  },
});

const { consent, fieldErrors, requestError, generating } = useDirectoryPdfExportStore();

const classModifiers = computed(() => ({
  "--is-generating": generating.value,
}));

const showCapWarning = computed(() => props.recordCap > 0 && props.totalCount > props.recordCap);
const capWarningText = computed(() => t("cap_warning", { total: props.totalCount, cap: props.recordCap }));

const requestErrorText = computed(() => {
  if (!requestError.value) {
    return null;
  }
  return requestError.value === "rate_limited" ? t("rate_limited_error") : t("generic_error");
});
</script>

<style lang="scss" scoped>
.export-form {
  position: relative;
  display: grid;
  align-content: start;
  gap: var(--sp-ref-scale-24);
  min-height: 100%;
  color: var(--sp-sys-color-on-surface);
  // Text alignment inherited from the host's surroundings must not restyle the
  // drawer content.
  text-align: start;

  &__fields {
    display: grid;
    gap: var(--sp-ref-scale-24);
  }

  &.--is-generating &__fields {
    opacity: var(--sp-comp-directory-pdf-generating-opacity, 0.55);
  }

  &__intro {
    margin: 0;
  }

  &__error {
    font-size: var(--sp-ref-font-size-small);
    color: var(--sp-comp-directory-pdf-error-text-color, var(--sp-sys-color-error));
  }

  &__consent {
    display: grid;
    gap: var(--sp-ref-scale-4);
  }

  &__generating {
    --sp-comp-progress-size-display: var(--sp-comp-directory-pdf-spinner-size, var(--sp-ref-scale-112));

    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
  }
}
</style>
